import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "../ui/button";
import {
  Upload,
  Loader2,
  CheckCircle2,
  XCircle,
  Building2,
  CreditCard,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import useSubjectExtraction from "./useSubjectExtraction";
import useVigenciaRules from "./useVigenciaRules";

const API = process.env.REACT_APP_BACKEND_URL;

// Documents required to leave this phase. ine_back is optional.
const REQUIRED_DOCS = ["ine_front", "constancia_fiscal"];
const ALL_TRACKED_DOCS = ["ine_front", "ine_back", "constancia_fiscal"];

const DOC_LABELS = {
  ine_front: "INE/Pasaporte (frente)",
  ine_back: "INE (vuelta)",
  constancia_fiscal: "Constancia de Situación Fiscal",
};

/**
 * Document upload phase.
 *
 * UX contract:
 *  1. User uploads each document.
 *  2. Once all required docs are uploaded, the Continue button changes to
 *     "Extrayendo datos..." with a per-document progress list.
 *  3. The button enables only when extraction finishes (done | failed) for
 *     every uploaded document — never blocking the user indefinitely.
 *  4. Extracted data is forwarded to the next phase via onNext, so the
 *     Review step lands with all fields pre-filled.
 */
export default function PhaseDocuments({ data, onNext, onBack, loading, draftId }) {
  const [uploading, setUploading] = useState({});
  const [uploaded, setUploaded] = useState({});
  // Bumping this re-arms the polling 45s budget after every new upload
  // (handled inside useSubjectExtraction). Without it, a doc uploaded
  // AFTER a previous batch already finished would inherit the old
  // timer and could be cut off in <1s.
  const [refreshKey, setRefreshKey] = useState(0);

  const { ocrEnabled } = useVigenciaRules();

  // True only when at least one required doc is uploaded; gate polling on it.
  const allRequiredUploaded = REQUIRED_DOCS.every((d) => uploaded[d]);
  // We also kick off polling as soon as ANY doc has been uploaded — that
  // way the user sees progress for ine_back even before they upload the
  // (required) CSF. Otherwise an ine_back uploaded BEFORE csf appears to
  // hang because polling is gated on allRequiredUploaded.
  const anyUploaded = Object.keys(uploaded).length > 0;

  // ── Polling delegated to the shared hook (single source of truth
  //    shared with the PM phases). PF passes subjectType=null so the
  //    endpoint returns every doc in the draft, exactly as before.
  const extraction = useSubjectExtraction({
    draftId,
    subjectType: null,
    subjectDraftId: null,
    enabled: anyUploaded,
    ocrEnabled,
    refreshKey,
  });
  const extractionState = {
    all_done: extraction.allDone,
    any_failed: extraction.anyFailed,
    documents: extraction.documents,
    merged_data: extraction.mergedData,
  };
  const elapsedSec = extraction.elapsedSec;

  const handleFileUpload = async (docType, file) => {
    if (!draftId) {
      toast.error("No se pudo guardar el draft. Recarga la página.");
      return;
    }
    setUploading((prev) => ({ ...prev, [docType]: true }));

    try {
      const form = new FormData();
      form.append("file", file);
      form.append("doc_type", docType);
      form.append("extract", "true");

      await axios.post(
        `${API}/api/customer-drafts/${draftId}/upload-document`,
        form,
        { withCredentials: true }
      );

      setUploaded((prev) => ({ ...prev, [docType]: file }));

      // A new upload re-opens extraction — bump refreshKey so the
      // shared hook resets its 45s budget and re-arms polling.
      setRefreshKey((k) => k + 1);
      toast.success(`${DOC_LABELS[docType] || docType} subido`);
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err?.response?.data?.detail || "Error al subir el documento");
    } finally {
      setUploading((prev) => ({ ...prev, [docType]: false }));
    }
  };

  const handleContinue = () => {
    if (!REQUIRED_DOCS.every((d) => uploaded[d])) {
      toast.error("Sube el documento de identidad y la Constancia de Situación Fiscal");
      return;
    }
    if (!extractionState.all_done) return; // safety; button is disabled anyway

    // Forward extracted data so the Review step lands fully pre-filled.
    // PR1.5 safeguard: also forward the list of fields the OCR thinks
    // need human verification (CURP/RFC from gpt-4o-mini fallback,
    // etc.). The Review step uses ``__review_fields`` to render an
    // amber "verifica" cue next to those inputs. Underscore prefix so
    // the field is recognisable as out-of-band metadata, not part of
    // the customer record. ``reviewFields`` may be empty/undefined on
    // failure paths or pre-PR1.5 backends — guard with ``|| []``.
    onNext({
      ...(extractionState.merged_data || {}),
      csf_uploaded: true,
      __review_fields: extraction.reviewFields || [],
    });
  };

  const DocUploadCard = ({ id, label, sublabel, icon: Icon, iconColor, busy, file }) => (
    <div
      className="border-2 border-dashed border-slate-700 rounded-xl p-5 text-center hover:border-slate-600 transition-colors"
      data-testid={`doc-card-${id}`}
    >
      <Icon className={`mx-auto mb-2 ${iconColor}`} size={28} />
      <h3 className="text-white font-medium mb-1 text-sm">{label}</h3>
      {sublabel && <p className="text-slate-500 text-xs mb-3">{sublabel}</p>}
      <input
        type="file"
        accept="image/*,application/pdf"
        onChange={(e) => e.target.files[0] && handleFileUpload(id, e.target.files[0])}
        className="hidden"
        id={`doc-${id}`}
        disabled={busy}
        data-testid={`doc-input-${id}`}
      />
      <label
        htmlFor={`doc-${id}`}
        data-testid={`doc-label-${id}`}
        className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
          busy
            ? "border-slate-600 bg-slate-700 text-slate-400 cursor-not-allowed"
            : file
              ? "border-teal-500 bg-teal-500/10 text-teal-400 cursor-pointer hover:bg-teal-500/20"
              : "border-slate-700 bg-transparent text-white cursor-pointer hover:bg-slate-800"
        }`}
      >
        {busy ? (
          <>
            <Loader2 size={14} className="mr-1.5 animate-spin" />
            Subiendo...
          </>
        ) : file ? (
          <>
            <CheckCircle2 size={14} className="mr-1.5 text-teal-400" />
            Subido ✓
          </>
        ) : (
          <>
            <Upload size={14} className="mr-1.5" />
            Seleccionar archivo
          </>
        )}
      </label>
    </div>
  );

  // ── Derived state for the Continue area ────────────────────────────────
  const docsState = extractionState.documents || {};
  // Documents the user has actually uploaded (filter out unknowns).
  const uploadedDocTypes = ALL_TRACKED_DOCS.filter((d) => uploaded[d]);
  const isExtracting =
    anyUploaded && !extractionState.all_done && uploadedDocTypes.length > 0;
  const continueDisabled =
    loading ||
    !allRequiredUploaded ||
    Object.values(uploading).some(Boolean) ||
    isExtracting;

  const renderProgressLine = (docType) => {
    const state = docsState[docType] || {};
    const status = state.status || "processing";
    const errorMsg = state.error;
    const label = DOC_LABELS[docType];
    if (status === "done") {
      return (
        <li key={docType} className="flex items-center gap-2 text-teal-400">
          <CheckCircle2 size={14} />
          <span>{label} lista</span>
        </li>
      );
    }
    // PR1: distinguishable failure reasons. ``failed_unreadable`` and
    // ``failed_unsupported`` are still amber (user can keep going via
    // manual capture) but the copy tells them WHY the OCR fell back so
    // they don't blame the system or retry the same blurry photo.
    // ``skipped_plan`` / ``skipped_unsupported`` are slate (informational,
    // no error tone) — the upload itself succeeded.
    const FAIL_COPY = {
      failed_unreadable:
        "no pudimos leer el documento — captúralo a mano abajo",
      failed_unsupported:
        "no se reconoció el tipo de documento — verifica que sea el correcto",
      failed_mismatch:
        "el documento subido no corresponde al tipo de este campo",
      failed: "no pudimos extraer datos — captúralo a mano",
    };
    if (status === "failed" || status === "failed_unreadable" || status === "failed_unsupported" || status === "failed_mismatch") {
      return (
        <li
          key={docType}
          className="flex items-start gap-2 text-amber-400"
          data-extraction-status={status}
        >
          <XCircle size={14} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div>{label} — {FAIL_COPY[status] || FAIL_COPY.failed}</div>
            {errorMsg && (
              <div
                data-testid={`extraction-error-${docType}`}
                className="text-[11px] text-amber-500/80 mt-0.5 font-mono truncate"
                title={errorMsg}
              >
                {errorMsg}
              </div>
            )}
          </div>
        </li>
      );
    }
    if (status === "skipped_plan" || status === "skipped_quota") {
      const isQuota = status === "skipped_quota";
      return (
        <li
          key={docType}
          className="flex items-start gap-2 text-slate-400"
          data-extraction-status={status}
        >
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>
            {isQuota
              ? `${label} guardada — alcanzaste el límite mensual de OCR de tu organización. Captura los campos a mano; el archivo se conservó en el expediente.`
              : `${label} subida — el OCR automático requiere plan Pro.`}
          </span>
        </li>
      );
    }
    if (status === "skipped_unsupported" || status === "skipped") {
      return (
        <li
          key={docType}
          className="flex items-center gap-2 text-slate-400"
          data-extraction-status={status}
        >
          <CheckCircle2 size={14} />
          <span>{label} guardada (sin OCR)</span>
        </li>
      );
    }
    return (
      <li key={docType} className="flex items-center gap-2 text-slate-400">
        <Loader2 size={14} className="animate-spin" />
        <span>{label} procesando...</span>
      </li>
    );
  };

  return (
    <div className="space-y-6" data-testid="phase-documents">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-white mb-2">Documentos de Identidad</h2>
        <p className="text-slate-400 text-sm">
          Sube el documento de identidad (INE/Pasaporte) y la Constancia de Situación Fiscal. Vamos
          a leer los datos automáticamente para que solo los confirmes en el siguiente paso.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
        <DocUploadCard
          id="ine_front"
          label="Documento - Frente *"
          sublabel="INE/IFE o Pasaporte"
          icon={CreditCard}
          iconColor="text-slate-400"
          busy={!!uploading.ine_front}
          file={uploaded.ine_front}
        />
        <DocUploadCard
          id="ine_back"
          label="Documento - Vuelta (opcional)"
          sublabel="Solo si aplica (INE/IFE)"
          icon={CreditCard}
          iconColor="text-slate-400"
          busy={!!uploading.ine_back}
          file={uploaded.ine_back}
        />
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-5 text-center">
          <Building2 className="mx-auto mb-2 text-amber-400" size={28} />
          <h3 className="text-white font-medium mb-1 text-sm">
            Constancia de Situación Fiscal (CSF) *
          </h3>
          <p className="text-slate-500 text-xs mb-3">
            Obligatoria — el RFC y régimen fiscal se extraerán automáticamente
          </p>
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) =>
              e.target.files[0] && handleFileUpload("constancia_fiscal", e.target.files[0])
            }
            className="hidden"
            id="doc-constancia_fiscal"
            disabled={!!uploading.constancia_fiscal}
            data-testid="doc-input-constancia_fiscal"
          />
          <label
            htmlFor="doc-constancia_fiscal"
            data-testid="doc-label-constancia_fiscal"
            className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
              uploading.constancia_fiscal
                ? "border-slate-600 bg-slate-700 text-slate-400 cursor-not-allowed"
                : uploaded.constancia_fiscal
                  ? "border-amber-500 bg-amber-500/10 text-amber-400 cursor-pointer hover:bg-amber-500/20"
                  : "border-amber-500/40 bg-transparent text-white cursor-pointer hover:bg-amber-500/10"
            }`}
          >
            {uploading.constancia_fiscal ? (
              <>
                <Loader2 size={14} className="mr-1.5 animate-spin" />
                Subiendo...
              </>
            ) : uploaded.constancia_fiscal ? (
              <>
                <CheckCircle2 size={14} className="mr-1.5 text-amber-400" />
                Subido ✓
              </>
            ) : (
              "Seleccionar archivo"
            )}
          </label>
        </div>
      </div>

      {/* Continue area */}
      <div className="flex flex-col items-stretch gap-3 max-w-2xl mx-auto">
        <div className="flex justify-between items-center">
          <Button
            onClick={onBack}
            variant="outline"
            className="border-slate-700"
            data-testid="back-btn"
          >
            ← Atrás
          </Button>
          <Button
            onClick={handleContinue}
            disabled={continueDisabled}
            className="bg-teal-600 hover:bg-teal-500 text-white min-w-[200px]"
            data-testid="continue-btn"
          >
            {isExtracting ? (
              <>
                <Loader2 className="animate-spin mr-2" size={14} />
                Extrayendo datos... {elapsedSec > 0 && `(${elapsedSec}s)`}
              </>
            ) : (
              "Continuar →"
            )}
          </Button>
        </div>

        {/* Per-document progress shown ONLY while extracting or when something failed */}
        {(isExtracting || extractionState.any_failed) && uploadedDocTypes.length > 0 && (
          <div
            className="bg-slate-800/40 border border-slate-700/60 rounded-lg p-3"
            data-testid="extraction-progress"
          >
            <ul className="text-xs space-y-1.5">
              {uploadedDocTypes.map(renderProgressLine)}
            </ul>
            {isExtracting && elapsedSec >= 15 && (
              <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-700/60">
                La extracción de imágenes (INE/Pasaporte) suele tomar 5-15s. Si tarda más,
                te dejaremos continuar y podrás llenar los datos faltantes manualmente.
              </p>
            )}
            {extractionState.all_done && extractionState.any_failed && (
              <div className="flex items-start gap-1.5 mt-2 pt-2 border-t border-slate-700/60 text-xs text-amber-400">
                <AlertCircle size={12} className="mt-0.5 shrink-0" />
                <span>
                  Algunos campos no se pudieron extraer. Podrás llenarlos manualmente en el
                  siguiente paso.
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
